import { isSmallDesktop } from "@hooks/layout";

import styled, { css, keyframes } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ── Account Banner ── */

export const AccountBanner = styled.div`
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  padding: 24px 40px;
  margin-bottom: 0;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(235, 194, 142, 0.1);
`;

export const AccountBannerInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

export const AccountBannerHeading = styled.h1`
  font-family: "Raleway", sans-serif;
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.2;
  color: #ffffff;
  margin: 0;
`;

/* ── Main Layout ── */

export const AccountContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  min-height: calc(100vh - 200px);
  gap: 0;

  /* Phone + tablet (<1150, the header-swap boundary): the left sidebar becomes
     a horizontal pill menu on top. Desktop ≥1150 keeps the left sidebar. */
  @media (max-width: 1149.98px) {
    flex-direction: column;
  }
`;

/* ── Sidebar ── */

export const Sidebar = styled.aside`
  width: 220px;
  flex-shrink: 0;
  padding: 39px 0 28px 0;
  border-right: 1px solid rgba(235, 194, 142, 0.1);

  @media (max-width: 1149.98px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid rgba(235, 194, 142, 0.1);
    padding: 16px 0;
  }
`;

export const SidebarSection = styled.div`
  margin-bottom: 24px;
  padding: 0 12px 0 0;

  /* Drop the asymmetric right padding below the boundary so the pill scroller's
     parent is symmetric/centred — that's what makes the FAQ full-bleed formula
     (calc(50% - 50vw) / calc(50vw - 50%)) rest the first pill exactly on the
     page's content gutter. */
  @media (max-width: 1149.98px) {
    margin-bottom: 0;
    padding: 0;
  }
`;

export const SidebarSectionTitle = styled.h5`
  font-family: "Raleway", sans-serif;
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: rgba(251, 244, 236, 0.35);
  padding: 0 0 8px 0;
  margin: 0;

  @media (max-width: 1149.98px) {
    display: none;
  }
`;

export const SidebarItems = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  border-left: 1px solid rgba(235, 194, 142, 0.1);

  /* Phone + tablet: single-row, full-bleed horizontal pill scroller — the exact
     mechanics of the home FAQ tab pills. Breaks out to the full viewport width
     so the pills can scroll past the screen edges, but rests with the FIRST pill
     aligned to the page's content gutter (calc(50vw - 50%)). Only the right side
     bleeds at rest; the left bleeds once you swipe. The PageContainer's
     overflow-x:hidden clips the 100vw so the page gains no horizontal scrollbar.
     Scrollbar hidden — it's a touch-swipe affordance. */
  @media (max-width: 1149.98px) {
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
    border-left: none;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    width: 100vw;
    max-width: 100vw;
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
    /* Symmetric content-gutter padding on BOTH sides so the first pill rests
       at the content edge AND, once fully swiped, the last pill keeps the
       same gutter instead of sticking to the viewport edge. */
    padding: 4px calc(50vw - 50%) 4px calc(50vw - 50%);
    justify-content: flex-start;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const SidebarItem = styled.li<{ $active?: boolean }>`
  a {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    margin-left: -1px;
    border-left: 2px solid ${({ $active }) => ($active ? "#9cd3fd" : "transparent")};
    color: ${({ $active }) => ($active ? "#fbf4ec" : "rgba(251, 244, 236, 0.7)")};
    font-family: "Raleway", sans-serif;
    font-size: 0.9375rem;
    font-weight: ${({ $active }) => ($active ? 500 : 400)};
    text-decoration: none;
    transition: all 0.15s ease;
    border-radius: 0 6px 6px 0;
    cursor: pointer;

    &:hover {
      color: #fbf4ec;
      border-left-color: ${({ $active }) => ($active ? "#9cd3fd" : "rgba(251, 244, 236, 0.25)")};
      background: rgba(255, 255, 255, 0.04);
    }

    svg {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
      color: ${({ $active }) => ($active ? "#9cd3fd" : "inherit")};
      transition: all 0.15s ease;
    }
  }

  /* Phone + tablet: FAQ-style pills (rounded, bordered, gold-glow when active)
     with a fixed width so they never shrink inside the scroller, plus a blue
     icon in front. */
  @media (max-width: 1149.98px) {
    flex: 0 0 auto;

    a {
      flex: 0 0 auto;
      height: 38px;
      gap: 8px;
      padding: 0 16px;
      margin-left: 0;
      border-radius: 20px;
      white-space: nowrap;
      font-size: 0.875rem;
      border: 1px solid ${({ $active }) => ($active ? "rgba(255, 210, 155, 0.65)" : "rgba(186, 144, 92, 0.25)")};
      background: ${({ $active }) => ($active ? "rgba(255, 220, 168, 0.18)" : "rgba(255, 255, 255, 0.05)")};
      color: ${({ $active }) => ($active ? "#fbf4ec" : "rgba(251, 244, 236, 0.65)")};
      box-shadow: ${({ $active }) => ($active ? "0 0 16px rgba(186, 144, 92, 0.28)" : "none")};

      &:hover {
        color: #fbf4ec;
        background: ${({ $active }) => ($active ? "rgba(255, 220, 168, 0.18)" : "rgba(255, 255, 255, 0.05)")};
        border-color: ${({ $active }) => ($active ? "rgba(255, 210, 155, 0.65)" : "rgba(186, 144, 92, 0.45)")};
      }

      /* Blue icon in front of every pill, regardless of active state. */
      svg {
        color: #9cd3fd;
      }
    }
  }
`;

export const SidebarBadge = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 10px;
  font-weight: 600;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #9cd3fd;
  color: #193e5b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 18px;
  margin-left: 6px;
`;

/* ── Title block (page heading + optional description/dev rail) ── */

export const TitleBlock = styled.div<{ $tinted?: boolean }>`
  padding-bottom: 20px;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(235, 194, 142, 0.1);

  /* Phone + tablet: drop the divider under the page heading for a cleaner,
     lighter header (desktop ≥1150 keeps the rule). */
  @media (max-width: 1149.98px) {
    padding-bottom: 0;
    border-bottom: none;
    /* Tinted-header pages (claims): the heading lives inside the darker
       HeaderBandOuter band wrapper, which continues seamlessly into the
       toolbar band below — so flatten the title's bottom margin to keep the
       band continuous (no page-bg gap between the heading and the warnings/
       picker below it). */
    ${({ $tinted }) =>
      $tinted &&
      css`
        margin-bottom: 0;
      `}
  }
`;

/* ── Tinted header band (phone/tablet only) ──
   Wraps the page's contentBanner (subscription notices) + the title block in
   one darker, full-VIEWPORT-width band. It sits a little below the site header
   (proper spacing, not touching it) and continues seamlessly into the claims
   toolbar band (`HeaderBand` in LicensesTab) below — together they wrap the
   notices, heading, warning banners and channel picker. Desktop (≥1150) is a
   plain passthrough. */
export const HeaderBandOuter = styled.div`
  @media (max-width: 1149.98px) {
    background: rgba(0, 0, 0, 0.2);
    width: 100vw;
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
    box-sizing: border-box;
    padding: 16px 16px 0;
  }
  @media (min-width: 1024px) and (max-width: 1149.98px) {
    padding-left: 40px;
    padding-right: 40px;
  }
`;

/* ── Content Area ── */

export const ContentArea = styled.main<{ $tintedHeader?: boolean }>`
  flex: 1;
  min-width: 0;
  padding: 32px 0 28px 40px;
  animation: ${fadeUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  /* No left sidebar below the boundary → no left gutter to clear. */
  @media (max-width: 1149.98px) {
    padding: 24px 0 24px 0;
    /* Tinted-header pages: drop the top padding so the heading band can run
       all the way up to the site header. */
    ${({ $tintedHeader }) => $tintedHeader && css`padding-top: 0;`}
  }
`;

export const ContentTitle = styled.h1`
  font-family: "Cormorant Garamond", serif;
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 24px;
  padding-left: 5px;
`;

/* ── Plan Status Banner ── */

export const PlanBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  background: rgba(75, 55, 39, 0.35);
  border: 1px solid rgba(186, 144, 92, 0.08);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 0;

  @media (max-width: 860px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

export const PlanInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const PlanIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(156, 211, 253, 0.1);
  border: 1px solid rgba(156, 211, 253, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
    stroke: #9cd3fd;
    fill: none;
    stroke-width: 1.5;
  }
`;

export const PlanDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const PlanName = styled.span`
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #fbf4ec;
`;

export const PlanMeta = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 0.8125rem;
  font-weight: 400;
  color: rgba(251, 244, 236, 0.5);
`;

export const PlanQuota = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const QuotaBar = styled.div`
  width: 120px;
  height: 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;

  @media (max-width: 480px) {
    width: 100px;
  }
`;

export const QuotaFill = styled.div<{ $percent: number }>`
  width: ${({ $percent }) => $percent}%;
  height: 100%;
  border-radius: 3px;
  background: ${({ $percent }) => ($percent > 80 ? "#e47a6a" : "#9cd3fd")};
  transition: width 0.3s ease;
`;

export const QuotaText = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 0.8125rem;
  font-weight: 500;
  color: rgba(251, 244, 236, 0.7);
  white-space: nowrap;
`;

export const NoPlanText = styled.span`
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  font-weight: 400;
  color: rgba(251, 244, 236, 0.5);
`;

export const UpgradeLink = styled.a`
  font-family: "Raleway", sans-serif;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #9cd3fd;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: #b0dfff;
  }
`;

/* ── Account Page Header ── */

export const AccountAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(235, 194, 142, 0.12);
  border: 1px solid rgba(235, 194, 142, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Cormorant Garamond", serif;
  font-size: 1.25rem;
  font-weight: 600;
  color: #ffdca8;
  flex-shrink: 0;
`;

export const AccountPageHeading = styled.h1`
  font-family: "Cormorant Garamond", serif;
  font-size: 2rem;
  font-weight: 400;
  line-height: 1.2;
  color: #ffffff;
  margin: 0;

  ${isSmallDesktop} {
    font-size: 2.5rem;
  }
`;

export const AccountGoldDivider = styled.div`
  width: 200px;
  height: 1px;
  background: linear-gradient(93deg, #ba905c 0.82%, #ffd29b 33.73%, #ffe0b1 47.65%, #ffd29b 61.53%, #ba905c 99.48%);
`;

export const AccountPageDescription = styled.p`
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  font-weight: 400;
  line-height: 1.7;
  color: #e0d2c1;
  text-align: center;
  max-width: 550px;
  margin: 0;
`;
